/**
 * Pure helpers for the AI Analyst (grouping, parsing, summaries, formatting),
 * ported from VentaHub's `pages/AiAnalyst/utils.ts` and the helpers its
 * components exported (`chartDescription`, `answerMarkdown`,
 * `describeChatError`). Kept apart from the components so Fast Refresh works
 * on them and each is tested on its own.
 *
 * Every word goes through `t` and every number or date through `lib/format`,
 * so nothing here hard-codes English or a locale.
 */
import type { TFunction } from 'i18next'

import { errorMessage, errorRequestId, errorType } from '@/lib/errors'
import { slugify } from '@/lib/slugify'
import { formatClock, formatDayMonth, formatNumber, formatTime, weekdayNames } from '@/lib/format'

import type {
  AnalystMessage,
  ChartData,
  Conversation,
  ConversationDetail,
  DebugEntry,
  ProgressData,
  ScopeOption,
  ScopeOptions,
  ScopePins,
  SnapshotData,
  StatusData,
  TableData,
  TraceLike,
} from './types'

export { slugify }

const DAY_MS = 24 * 3600 * 1000

/** An empty cell, as every table in the app writes it. */
export const EMPTY_CELL = '—'

/** Human display of one cell value. */
export function formatCell(value: unknown, t: TFunction, locale = 'en'): string {
  if (value === null || value === undefined || value === '') return EMPTY_CELL
  if (typeof value === 'number') return formatNumber(value, locale)
  if (typeof value === 'boolean') return value ? t('aiAnalyst.cell.yes') : t('aiAnalyst.cell.no')
  return plainText(value)
}

/** A raw value as text: objects as JSON, anything else as itself. */
function plainText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint')
    return String(value)
  return JSON.stringify(value) ?? ''
}

export interface ProgressStep extends ProgressData {
  id: string
}

/** The progress steps of an assistant message, in order. */
export function progressSteps(message: AnalystMessage): ProgressStep[] {
  const steps: ProgressStep[] = []
  message.parts.forEach((part, index) => {
    if (part.type === 'data-progress') steps.push({ id: part.id ?? String(index), ...part.data })
  })
  return steps
}

/**
 * Parts as stored (a message's, or a trace's answer): exactly as they were
 * streamed. OpenAPI documents them only as objects; their shapes are the
 * stream protocol's, typed in `types.ts`, so this is the one place the two meet.
 */
export function storedParts(parts: Record<string, unknown>[]): AnalystMessage['parts'] {
  return parts as AnalystMessage['parts']
}

/** Stored messages as AI SDK UI messages. */
export function toUIMessages(detail: ConversationDetail): AnalystMessage[] {
  return detail.messages.map((m) => ({
    id: m.id,
    role: m.role,
    parts: storedParts(m.parts),
    metadata: { run_id: m.run_id, run_status: m.run_status ?? null, feedback: m.feedback ?? null },
  }))
}

export type ConversationGroupKey = 'pinned' | 'today' | 'previous7Days' | 'older'

export interface ConversationGroup {
  key: ConversationGroupKey
  label: string
  items: Conversation[]
}

/** Sidebar groups: Pinned, Today, Previous 7 days, Older (empty groups dropped). */
export function groupConversations(
  conversations: Conversation[],
  t: TFunction,
  now: Date = new Date(),
): ConversationGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const keys: ConversationGroupKey[] = ['pinned', 'today', 'previous7Days', 'older']
  const groups = keys.map((key) => ({
    key,
    label: t(`aiAnalyst.groups.${key}`),
    items: [] as Conversation[],
  }))
  for (const c of conversations) {
    const time = new Date(c.updated_at).getTime()
    if (c.pinned) groups[0]!.items.push(c)
    else if (time >= startOfToday) groups[1]!.items.push(c)
    else if (time >= startOfToday - 7 * DAY_MS) groups[2]!.items.push(c)
    else groups[3]!.items.push(c)
  }
  return groups.filter((g) => g.items.length > 0)
}

/** The options whose ids are pinned. */
export function pickOptions(
  options: ScopeOption[] | undefined,
  ids: number[] | null | undefined,
): ScopeOption[] {
  if (!options || !ids) return []
  return options.filter((o) => ids.includes(o.id))
}

export type PinKind = 'store' | 'brand' | 'period'

export interface PinChip {
  kind: PinKind
  label: string
}

function yearOf(iso: string): number {
  return Number(iso.slice(0, 4))
}

/** One calendar day, compactly: "16 Oct" (with the year when it is not `currentYear`). */
export function formatPinDay(
  iso: string,
  t: TFunction,
  locale = 'en',
  currentYear = new Date().getFullYear(),
): string {
  const day = formatDayMonth(iso.slice(0, 10), locale)
  const year = yearOf(iso)
  return year !== currentYear ? t('aiAnalyst.pins.dayWithYear', { day, year }) : day
}

/**
 * A pinned period in a few characters: "1–16 Oct", "28 Sep – 3 Oct", "From 1 Oct", "Until 16 Oct".
 * The year is shown only when it is not `currentYear`. Dates are calendar days, read without time zones.
 */
export function formatPeriod(
  from: string | null | undefined,
  to: string | null | undefined,
  t: TFunction,
  locale = 'en',
  currentYear = new Date().getFullYear(),
): string {
  const day = (iso: string) => formatPinDay(iso, t, locale, currentYear)
  if (from && to) {
    if (from.slice(0, 7) === to.slice(0, 7)) {
      return t('aiAnalyst.pins.sameMonthRange', {
        fromDay: formatNumber(Number(from.slice(8, 10)), locale),
        to: day(to),
      })
    }
    return t('aiAnalyst.pins.range', { from: day(from), to: day(to) })
  }
  if (from) return t('aiAnalyst.pins.from', { day: day(from) })
  return t('aiAnalyst.pins.until', { day: day(to ?? '') })
}

/** The pins as small labelled chips ("Store One", "2 brands", "1–16 Oct"), in store, brand, period order. */
export function pinChips(
  pins: ScopePins,
  options: ScopeOptions | undefined,
  t: TFunction,
  locale = 'en',
  currentYear = new Date().getFullYear(),
): PinChip[] {
  const out: PinChip[] = []
  const stores = pickOptions(options?.stores, pins.store_ids)
  const brands = pickOptions(options?.brands, pins.brand_ids)
  if (stores.length) {
    out.push({
      kind: 'store',
      label:
        stores.length === 1
          ? stores[0]!.name
          : t('aiAnalyst.pins.stores', { count: stores.length }),
    })
  }
  if (brands.length) {
    out.push({
      kind: 'brand',
      label:
        brands.length === 1
          ? brands[0]!.name
          : t('aiAnalyst.pins.brands', { count: brands.length }),
    })
  }
  if (pins.date_from || pins.date_to) {
    out.push({
      kind: 'period',
      label: formatPeriod(pins.date_from, pins.date_to, t, locale, currentYear),
    })
  }
  return out
}

/** `pins` without one kind of pin (removing a store chip keeps the brand and period pins). */
export function withoutPin(pins: ScopePins, kind: PinKind): ScopePins {
  if (kind === 'store') return { ...pins, store_ids: undefined }
  if (kind === 'brand') return { ...pins, brand_ids: undefined }
  return { ...pins, date_from: undefined, date_to: undefined }
}

/** When a conversation was last active, as short as possible: "14:05" today, "Mon" this week, "3 Oct" otherwise. */
export function shortWhen(
  iso: string,
  t: TFunction,
  locale = 'en',
  now: Date = new Date(),
): string {
  const when = new Date(iso)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  if (when.getTime() >= startOfToday) return formatTime(iso, locale)
  if (when.getTime() >= startOfToday - 6 * DAY_MS) return weekdayNames(locale)[when.getDay()]!
  const pad = (n: number) => String(n).padStart(2, '0')
  const day = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`
  return formatPinDay(day, t, locale, now.getFullYear())
}

/** The `##` headings of a Markdown answer, for the "In this answer" outline. */
export function outlineOf(text: string): { title: string; id: string }[] {
  return Array.from(text.matchAll(/^##\s+(.+?)\s*#*$/gm)).map((m) => {
    const title = (m[1] ?? '').replace(/[*_`]/g, '').trim()
    return { title, id: slugify(title) }
  })
}

export interface StepGroup {
  step: ProgressStep
  children: ProgressStep[]
}

/**
 * Steps with each worker's own steps nested under its task: a worker's first
 * step is the task label (from `delegate`), the rest are its checks.
 */
export function groupSteps(steps: ProgressStep[]): StepGroup[] {
  const out: StepGroup[] = []
  const byAgent = new Map<string, StepGroup>()
  for (const step of steps) {
    const group = step.agent !== 'analyst' ? byAgent.get(step.agent) : undefined
    if (group) group.children.push(step)
    else {
      const created: StepGroup = { step, children: [] }
      out.push(created)
      if (step.agent !== 'analyst') byAgent.set(step.agent, created)
    }
  }
  return out
}

/**
 * The failed steps the analyst recovered from: a later step of the same agent
 * succeeded, so the failure was a check it corrected (a wrong column, a name
 * it then looked up), not a gap in the answer. Only the unrecovered ones are
 * failures worth a warning.
 */
export function recoveredSteps(steps: ProgressStep[]): Set<string> {
  const recovered = new Set<string>()
  steps.forEach((step, index) => {
    if (step.status !== 'error') return
    const later = steps.slice(index + 1)
    if (later.some((next) => next.agent === step.agent && next.status === 'done')) {
      recovered.add(step.id)
    }
  })
  return recovered
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/

/** Whether chart x values are dates (so ticks read "16 Oct"). */
export function isDateAxis(values: unknown[]): boolean {
  const present = values.filter((v) => v !== null && v !== undefined && v !== '')
  return present.length > 0 && present.every((v) => typeof v === 'string' && ISO_DAY.test(v))
}

/**
 * Rows with all but the largest `keep - 1` (by the first series) folded into
 * one row labelled `otherLabel` (bar and pie charts). The sums are of chart
 * values the backend already computed, for display only.
 */
export function foldOther(rows: unknown[][], otherLabel: string, keep = 8): unknown[][] {
  if (rows.length <= keep) return rows
  const value = (r: unknown[]) => Number(r[1]) || 0
  const ranked = [...rows].sort((a, b) => value(b) - value(a))
  const top = ranked.slice(0, keep - 1)
  const rest = ranked.slice(keep - 1)
  const width = (rows[0]?.length ?? 1) - 1
  const other: unknown[] = [
    otherLabel,
    ...Array.from({ length: width }, (_, i) =>
      rest.reduce<number>((sum, r) => sum + (Number(r[i + 1]) || 0), 0),
    ),
  ]
  return [...top, other]
}

/** Rows as tab-separated text with a header line (pastes into Excel, Sheets or Slack as a table). */
export function toTsv(headers: string[], rows: unknown[][]): string {
  const cell = (v: unknown) => plainText(v).replace(/[\t\n]/g, ' ')
  return [headers.map(cell).join('\t'), ...rows.map((r) => r.map(cell).join('\t'))].join('\n')
}

const CODE = /^[A-Za-z0-9][A-Za-z0-9._\-/]{1,39}$/
const HEADER_WORDS = new Set([
  'sku',
  'skus',
  'asin',
  'asins',
  'sku_code',
  'sku code',
  'code',
  'codes',
  'seller_sku',
  'msku',
])
export const MAX_ATTACHED_CODES = 500

/**
 * SKU or ASIN codes from pasted text or a CSV/TXT file: one per line, or
 * separated by commas, semicolons, tabs or spaces. In a CSV only the first
 * column counts. Header words ("SKU", "ASIN") and anything that does not look
 * like a code are dropped, duplicates removed, and at most 500 kept.
 */
export function parseCodes(text: string, csv = false): string[] {
  const tokens = csv
    ? text.split(/\r?\n/).map((line) => line.split(/[,;\t]/)[0] ?? '')
    : text.split(/[\s,;]+/)
  const out: string[] = []
  for (const raw of tokens) {
    const token = raw.trim().replace(/^["']|["']$/g, '')
    if (!token || HEADER_WORDS.has(token.toLowerCase()) || !CODE.test(token) || out.includes(token))
      continue
    out.push(token)
    if (out.length >= MAX_ATTACHED_CODES) break
  }
  return out
}

/** "usually about 25 s" / "usually about 3 min" from a tier's median duration; null when unknown. */
export function timingHint(
  tier: { p50_seconds?: number | null } | undefined,
  t: TFunction,
  locale = 'en',
): string | null {
  const seconds = tier?.p50_seconds
  if (!seconds) return null
  if (seconds < 90) {
    return t('aiAnalyst.timing.seconds', {
      seconds: formatNumber(Math.max(5, Math.round(seconds / 5) * 5), locale),
    })
  }
  return t('aiAnalyst.timing.minutes', { minutes: formatNumber(Math.round(seconds / 60), locale) })
}

export type ExampleTopic = 'sales' | 'ads' | 'listings' | 'stock' | 'payouts' | 'ops'

export const EXAMPLE_TOPICS: readonly ExampleTopic[] = [
  'sales',
  'ads',
  'listings',
  'stock',
  'payouts',
  'ops',
]

/**
 * Example questions by topic for the welcome screen, worded for the
 * organisation's first marketplace (VentaHub's `examplesFor`).
 */
export function examplesFor(marketplaces: string[], t: TFunction): Record<ExampleTopic, string[]> {
  const marketplace = marketplaces[0] ?? t('aiAnalyst.examples.defaultMarketplace')
  return {
    sales: [
      t('aiAnalyst.examples.sales.weekOverWeek', { marketplace }),
      t('aiAnalyst.examples.sales.growersDroppers'),
      t('aiAnalyst.examples.sales.marginByStore'),
      t('aiAnalyst.examples.sales.driveMoreSales'),
      t('aiAnalyst.examples.sales.projectedSku'),
      t('aiAnalyst.examples.sales.projectedChannel'),
    ],
    ads: [
      t('aiAnalyst.examples.ads.wastedSpend'),
      t('aiAnalyst.examples.ads.acosTacos'),
      t('aiAnalyst.examples.ads.adGroupsExcess'),
      t('aiAnalyst.examples.ads.targetsExcess'),
      t('aiAnalyst.examples.ads.targetsIncrease'),
      t('aiAnalyst.examples.ads.targetsReduce'),
      t('aiAnalyst.examples.ads.strategy'),
      t('aiAnalyst.examples.ads.slotShare'),
    ],
    listings: [
      t('aiAnalyst.examples.listings.competitorAsins'),
      t('aiAnalyst.examples.listings.topCompetitors'),
      t('aiAnalyst.examples.listings.pricedAbove'),
      t('aiAnalyst.examples.listings.rightPrice'),
      t('aiAnalyst.examples.listings.ratingIssues'),
      t('aiAnalyst.examples.listings.catalogueIssues'),
    ],
    stock: [
      t('aiAnalyst.examples.stock.runOut'),
      t('aiAnalyst.examples.stock.unsellable', { marketplace }),
      t('aiAnalyst.examples.stock.inTransit'),
      t('aiAnalyst.examples.stock.notSupplied'),
      t('aiAnalyst.examples.stock.oosLoss'),
    ],
    payouts: [
      t('aiAnalyst.examples.payouts.paidOut', { marketplace }),
      t('aiAnalyst.examples.payouts.biggestFees'),
      t('aiAnalyst.examples.payouts.unsettled'),
    ],
    ops: [
      t('aiAnalyst.examples.ops.behindSchedule'),
      t('aiAnalyst.examples.ops.lateVendors'),
      t('aiAnalyst.examples.ops.slaBreaches'),
    ],
  }
}

/** `m:ss` for the live timer. */
export function formatElapsed(ms: number, locale = 'en'): string {
  return formatClock(Math.floor(Math.max(0, ms) / 1000), locale)
}

/** The text of a message (for "Dig deeper", which re-asks it). */
export function messageText(message: AnalystMessage): string {
  return message.parts
    .map((p) => (p.type === 'text' ? p.text : ''))
    .join('')
    .trim()
}

/** The label the status line shows before the server's first status. */
export const CONNECTING_LABEL = 'Connecting'

/** The status line's text: the label, with an ellipsis unless it already ends a sentence. */
export function statusText(status: StatusData, t: TFunction): string {
  const raw = status.label.trim()
  const label =
    raw === CONNECTING_LABEL
      ? t('aiAnalyst.status.connecting')
      : raw || t('aiAnalyst.status.working')
  return /[.…!?]$/.test(label) ? label : t('aiAnalyst.status.ellipsis', { label })
}

/** Snapshot confidence (1-5) in words; anything else as the number. */
export function confidenceLabel(n: number, t: TFunction): string {
  const key = CONFIDENCE[n]
  return key ? t(`aiAnalyst.confidence.${key}`) : String(n)
}

const CONFIDENCE: Record<number, 'veryLow' | 'low' | 'medium' | 'high' | 'veryHigh'> = {
  1: 'veryLow',
  2: 'low',
  3: 'medium',
  4: 'high',
  5: 'veryHigh',
}

export type Severity = 'critical' | 'important' | 'opportunity'

/** Priority 1-3 is critical, 4-6 important, 7+ an opportunity (Weekly Insights severity). */
export function prioritySeverity(priority: number): Severity {
  if (priority <= 3) return 'critical'
  if (priority <= 6) return 'important'
  return 'opportunity'
}

/** A short description of the chart for screen readers. */
export function chartDescription(data: ChartData, t: TFunction): string {
  return t('aiAnalyst.chart.description', {
    kind: t(`aiAnalyst.chart.${data.type}`),
    series: data.series.map((s) => s.label).join(', '),
    x: data.x.label,
    count: data.rows.length,
  })
}

/** The admin trace as debug entries (steps, then a usage summary). */
export function traceToEntries(trace: TraceLike): DebugEntry[] {
  const steps: DebugEntry[] = trace.steps.map((s) => ({
    kind: s.kind,
    seq: s.seq,
    agent: s.agent,
    label: s.label,
    detail: s.detail,
    row_count: s.row_count,
    duration_ms: s.duration_ms,
    error: s.error,
  }))
  const models = trace.models
  const text = (value: unknown) =>
    typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  // After a fallback, `configured` names what settings asked for (llm/fallback.py).
  const configured = (models.configured ?? {}) as Record<string, unknown>
  steps.push({
    kind: 'usage',
    models: {
      analyst: text(models.analyst),
      worker: text(models.worker),
      ...Object.fromEntries(
        Object.entries(configured).map(([role, name]) => [`${role}_configured`, text(name)]),
      ),
    },
    depth: models.depth ? text(models.depth) : undefined,
    input_tokens: trace.tokens.input,
    output_tokens: trace.tokens.output,
    cache_read_tokens: trace.tokens.cache_read,
    cache_write_tokens: trace.tokens.cache_write,
    cost_usd: trace.row.cost_usd,
    queries: trace.row.query_count,
    duration_ms:
      trace.row.duration_seconds !== null
        ? Math.round(trace.row.duration_seconds * 1000)
        : undefined,
  })
  return steps
}

const NUMERIC_TEXT = /^-?\d+(\.\d+)?$/

/** A column is numeric when it has values and every non-empty one is a number (or numeric text). */
export function isNumericColumn(values: unknown[]): boolean {
  const present = values.filter((v) => v !== null && v !== undefined && v !== '')
  return (
    present.length > 0 &&
    present.every(
      (v) =>
        (typeof v === 'number' && !Number.isNaN(v)) ||
        (typeof v === 'string' && NUMERIC_TEXT.test(v)),
    )
  )
}

/** "Resets in 5h 20m" / "Resets in 12m" until `resetsAt` (never below 1 minute). */
export function resetsIn(resetsAt: string, t: TFunction, now: number = Date.now()): string {
  const minutes = Math.max(1, Math.ceil((new Date(resetsAt).getTime() - now) / 60000))
  const hours = Math.floor(minutes / 60)
  return hours > 0
    ? t('aiAnalyst.usage.resetsInHours', { hours, minutes: minutes % 60 })
    : t('aiAnalyst.usage.resetsInMinutes', { minutes })
}

export type UsageTone = 'calm' | 'warn' | 'bad'

/** Meter colour band: calm below 70%, a warning from 70%, red from 90%. */
export function usageTone(percent: number): UsageTone {
  if (percent >= 90) return 'bad'
  if (percent >= 70) return 'warn'
  return 'calm'
}

function mdCell(value: unknown, t: TFunction, locale: string): string {
  return formatCell(value, t, locale).replace(/\|/g, '/').replace(/\n/g, ' ')
}

function mdTable(
  title: string,
  headers: string[],
  rows: unknown[][],
  t: TFunction,
  locale: string,
): string {
  return [
    `**${title}**`,
    '',
    `| ${headers.join(' | ')} |`,
    `|${headers.map(() => '---').join('|')}|`,
    ...rows.map((r) => `| ${r.map((v) => mdCell(v, t, locale)).join(' | ')} |`),
  ].join('\n')
}

/** The answer as Markdown: its text, tables and chart data as Markdown tables, and a snapshot's insights. */
export function answerMarkdown(message: AnalystMessage, t: TFunction, locale = 'en'): string {
  const out: string[] = []
  for (const part of message.parts) {
    if (part.type === 'text') {
      if (part.text) out.push(part.text)
    } else if (part.type === 'data-table') {
      const d: TableData = part.data
      out.push(
        mdTable(
          d.title || t('aiAnalyst.markdown.result'),
          d.columns.map((c) => c.label),
          d.rows,
          t,
          locale,
        ),
      )
    } else if (part.type === 'data-chart') {
      const d: ChartData = part.data
      out.push(
        mdTable(
          t('aiAnalyst.markdown.chartData', { title: d.title || t('aiAnalyst.markdown.chart') }),
          [d.x.label, ...d.series.map((s) => s.label)],
          d.rows,
          t,
          locale,
        ),
      )
    } else if (part.type === 'data-snapshot') {
      const d: SnapshotData = part.data
      if (d.summary) out.push(d.summary)
      out.push(
        mdTable(
          t('aiAnalyst.markdown.insights'),
          [
            t('aiAnalyst.markdown.priority'),
            t('aiAnalyst.markdown.area'),
            t('aiAnalyst.markdown.finding'),
            t('aiAnalyst.markdown.doNext'),
          ],
          [...(d.insights ?? [])]
            .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
            .map((i) => [i.priority, i.area, i.finding, i.recommended_action]),
          t,
          locale,
        ),
      )
    }
  }
  return out.join('\n\n').trim()
}

/** The answer as plain text: Markdown markers removed, tables kept as tab-separated rows. */
export function answerPlain(message: AnalystMessage, t: TFunction, locale = 'en'): string {
  return answerMarkdown(message, t, locale)
    .split('\n')
    .filter((line) => !/^\|?\s*-{3}/.test(line) && !/^\|(---\|)+$/.test(line))
    .map((line) =>
      line.startsWith('|')
        ? line
            .replace(/^\|\s*|\s*\|$/g, '')
            .split(/\s*\|\s*/)
            .join('\t')
        : line,
    )
    .join('\n')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#+\s*/gm, '')
}

export type ChatErrorKind = 'busy' | 'limit' | 'other'

export interface ChatErrorView {
  message: string
  kind: ChatErrorKind
  requestId?: string
}

/** The envelope inside an `Error` whose message is the response body (how a stream request fails). */
function unwrapEnvelope(error: unknown): unknown {
  if (error instanceof Error) {
    try {
      const parsed: unknown = JSON.parse(error.message)
      if (typeof parsed === 'object' && parsed !== null && 'error' in parsed) return parsed
    } catch {
      // Not a JSON body: the error's own message is what there is to show.
    }
  }
  return error
}

function statusOf(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const { status } = error
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}

/**
 * A failed chat request, for display: the backend's message, what kind of
 * refusal it was, and the request id to quote. `POST /chat/stream` documents
 * its refusals as an `ErrorEnvelope` whose `error.type` says which:
 * `run_in_progress` (409) is "an answer is already being written" (offer
 * Reattach); `run_not_replaceable` (also 409) is a retry of a run that is no
 * longer the latest, an ordinary error; `quota_exceeded` (429) is the daily
 * limit (say when it resets).
 */
export function describeChatError(error: unknown, t: TFunction): ChatErrorView | null {
  if (!error) return null
  const envelope = unwrapEnvelope(error)
  const message = errorMessage(envelope, t('aiAnalyst.errors.generic'))
  const type = errorType(envelope) ?? ''
  const status = statusOf(error) ?? statusOf(envelope)
  let kind: ChatErrorKind = 'other'
  if (
    type === 'run_in_progress' ||
    (status === 409 && type !== 'run_not_replaceable') ||
    /already being written/i.test(message)
  )
    kind = 'busy'
  else if (type === 'quota_exceeded' || status === 429 || /limit/i.test(message)) kind = 'limit'
  return { message, kind, requestId: errorRequestId(envelope) }
}
